import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Save, Trash2, Edit, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { key: '', name: '', description: '', price_inr: 0, price_usd: 0, is_active: true, sort_order: 0 };

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs text-zinc-400 mb-1 block">{label}</span>{children}</label>
);

const inputCls = 'w-full border border-zinc-700 bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm';

const AddonForm = ({ initial, isNew, onSave, onCancel }) => {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : (e.target.type === 'number' ? Number(e.target.value) : e.target.value) });
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 grid md:grid-cols-3 gap-3" data-testid="addon-form">
      <Field label="Key (lowercase, e.g. event_invitations)"><input className={inputCls} value={form.key} onChange={set('key')} disabled={!isNew} data-testid="addon-form-key" /></Field>
      <Field label="Name"><input className={inputCls} value={form.name} onChange={set('name')} data-testid="addon-form-name" /></Field>
      <Field label="Sort order"><input type="number" className={inputCls} value={form.sort_order} onChange={set('sort_order')} data-testid="addon-form-sort" /></Field>
      <Field label="Price (INR)"><input type="number" min="0" className={inputCls} value={form.price_inr} onChange={set('price_inr')} data-testid="addon-form-price-inr" /></Field>
      <Field label="Price (USD)"><input type="number" min="0" className={inputCls} value={form.price_usd} onChange={set('price_usd')} data-testid="addon-form-price-usd" /></Field>
      <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={form.is_active} onChange={set('is_active')} data-testid="addon-form-active" /> Active (visible on /pricing)</label>
      <div className="md:col-span-3"><Field label="Description"><input className={inputCls} value={form.description} onChange={set('description')} data-testid="addon-form-description" /></Field></div>
      <div className="md:col-span-3 flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm rounded-lg border border-zinc-700 text-zinc-300 flex items-center gap-1" data-testid="addon-form-cancel"><X className="w-4 h-4" /> Cancel</button>
        <button type="button" onClick={() => onSave(form)} className="px-4 py-2 text-sm rounded-lg bg-[#C41E3A] text-white flex items-center gap-1" data-testid="addon-form-save"><Save className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
};

export default function AddonsManager() {
  const [addons, setAddons] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API_URL}/api/admin/addons`, { withCredentials: true }).then((r) => setAddons(r.data.addons || [])).catch(() => toast.error('Failed to load add-ons'));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing === 'new') await axios.post(`${API_URL}/api/admin/addons`, form, { withCredentials: true });
      else await axios.put(`${API_URL}/api/admin/addons/${form.key}`, form, { withCredentials: true });
      toast.success('Add-on saved');
      setEditing(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Please check all fields');
    }
  };

  const remove = async (key) => {
    if (!window.confirm('Delete this add-on? Users who already bought it keep access until expiry. If it gates a feature, nobody on Basic can buy it anymore.')) return;
    await axios.delete(`${API_URL}/api/admin/addons/${key}`, { withCredentials: true });
    toast.success('Add-on deleted');
    load();
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6" data-testid="addons-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-white">Add-on Services</h3>
          <p className="text-sm text-zinc-400">Pro features sold on /pricing (12 months each, Basic plan required). All-Access includes all of them.</p>
        </div>
        {!editing && <button type="button" onClick={() => setEditing('new')} className="px-4 py-2 text-sm rounded-lg bg-[#C41E3A] text-white flex items-center gap-1" data-testid="addon-add-btn"><Plus className="w-4 h-4" /> Add service</button>}
      </div>
      {editing === 'new' && <AddonForm initial={EMPTY} isNew onSave={save} onCancel={() => setEditing(null)} />}
      <div className="divide-y divide-zinc-800 mt-2">
        {addons.map((a) => (editing === a.key ? (
          <AddonForm key={a.key} initial={a} onSave={save} onCancel={() => setEditing(null)} />
        ) : (
          <div key={a.key} className="flex items-center justify-between py-3" data-testid={`admin-addon-${a.key}`}>
            <div>
              <p className="font-medium text-white">{a.name} {!a.is_active && <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded ml-2">Inactive</span>}</p>
              <p className="text-sm text-zinc-400">₹{a.price_inr.toLocaleString('en-IN')} / ${a.price_usd} · <code className="text-xs">{a.key}</code></p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setEditing(a.key)} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-300" data-testid={`admin-addon-edit-${a.key}`}><Edit className="w-4 h-4" /></button>
              <button type="button" onClick={() => remove(a.key)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400" data-testid={`admin-addon-delete-${a.key}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        )))}
      </div>
    </div>
  );
}
